import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Cube } from '@phosphor-icons/react';

export default function BatchCreateDialog({ open, onOpenChange, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);

  const [fabDate, setFabDate] = useState(today);
  const [resins, setResins] = useState([]);
  const [pigments, setPigments] = useState([]);
  const [batchModels, setBatchModels] = useState([]);
  const [postTreatments, setPostTreatments] = useState([]);
  const [selectedResinId, setSelectedResinId] = useState('');
  const [colorType, setColorType] = useState('Blanc');
  const [selectedPigmentId, setSelectedPigmentId] = useState('');
  const [selectedModels, setSelectedModels] = useState({});
  const [postTreatment, setPostTreatment] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [addResinOpen, setAddResinOpen] = useState(false);
  const [addPigmentOpen, setAddPigmentOpen] = useState(false);
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [newResin, setNewResin] = useState({ brand: '', model: '', reference: '' });
  const [newPigment, setNewPigment] = useState({ name: '', color_code: '', reference: '' });
  const [newModel, setNewModel] = useState({ name: '', description: '' });

  const fetchData = useCallback(async () => {
    try {
      const [r, p, m, t] = await Promise.all([
        axios.get(`${API}/resins`),
        axios.get(`${API}/pigments`),
        axios.get(`${API}/batch-models`),
        axios.get(`${API}/post-treatments`),
      ]);
      setResins(r.data);
      setPigments(p.data);
      setBatchModels(m.data);
      setPostTreatments(t.data);
    } catch (e) { toast.error('Erreur chargement données'); }
  }, []);

  useEffect(() => { if (open) { fetchData(); resetForm(); } }, [open, fetchData]);

  const resetForm = () => {
    setFabDate(today);
    setSelectedResinId('');
    setColorType('Blanc');
    setSelectedPigmentId('');
    setSelectedModels({});
    setPostTreatment('');
    setNotes('');
  };

  const toggleModel = (modelId, modelName) => {
    setSelectedModels(prev => {
      const next = { ...prev };
      if (next[modelId]) { delete next[modelId]; }
      else { next[modelId] = { model_id: modelId, model_name: modelName, quantity: 1 }; }
      return next;
    });
  };

  const setModelQty = (modelId, qty) => {
    setSelectedModels(prev => ({
      ...prev,
      [modelId]: { ...prev[modelId], quantity: Math.max(1, parseInt(qty) || 1) }
    }));
  };

  const handleAddResin = async () => {
    if (!newResin.brand || !newResin.model) { toast.error('Marque et nom requis'); return; }
    try {
      const res = await axios.post(`${API}/resins`, newResin);
      setResins(prev => [...prev, res.data]);
      setSelectedResinId(res.data.id);
      setNewResin({ brand: '', model: '', reference: '' });
      setAddResinOpen(false);
      toast.success('Résine ajoutée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAddPigment = async () => {
    if (!newPigment.name) { toast.error('Nom requis'); return; }
    try {
      const res = await axios.post(`${API}/pigments`, newPigment);
      setPigments(prev => [...prev, res.data]);
      setSelectedPigmentId(res.data.id);
      setColorType('pigment');
      setNewPigment({ name: '', color_code: '', reference: '' });
      setAddPigmentOpen(false);
      toast.success('Pigment ajouté');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAddModel = async () => {
    if (!newModel.name) { toast.error('Nom requis'); return; }
    try {
      const res = await axios.post(`${API}/batch-models`, newModel);
      setBatchModels(prev => [...prev, res.data]);
      setNewModel({ name: '', description: '' });
      setAddModelOpen(false);
      toast.success('Modèle ajouté');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSubmit = async () => {
    if (!selectedResinId) { toast.error('Sélectionnez une résine'); return; }
    if (!postTreatment) { toast.error('Sélectionnez un post-traitement'); return; }
    const modelEntries = Object.values(selectedModels);
    if (modelEntries.length === 0) { toast.error('Sélectionnez au moins un modèle'); return; }
    if (colorType === 'pigment' && !selectedPigmentId) { toast.error('Sélectionnez un pigment'); return; }

    setSubmitting(true);
    try {
      let colorName = colorType;
      if (colorType === 'pigment') {
        const pig = pigments.find(p => p.id === selectedPigmentId);
        colorName = pig ? pig.name : 'Personnalisé';
      }

      const payload = {
        fabrication_date: fabDate,
        resin_id: selectedResinId,
        pigment_id: colorType === 'pigment' ? selectedPigmentId : null,
        color_name: colorName,
        models: modelEntries,
        post_treatment: postTreatment,
        notes: notes || null
      };
      await axios.post(`${API}/batches`, payload);
      toast.success('Lot créé avec succès');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur création lot'); }
    finally { setSubmitting(false); }
  };

  const totalPieces = Object.values(selectedModels).reduce((s, m) => s + m.quantity, 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="batch-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Cube size={24} className="text-zinc-900" />Créer un lot de fabrication</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-5 py-2">
            {/* Date */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Date de fabrication</Label>
              <Input type="date" value={fabDate} onChange={e => setFabDate(e.target.value)} className="mt-1 rounded-sm" data-testid="batch-fab-date" />
            </div>

            {/* Resin */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Résine *</Label>
                <Button variant="ghost" size="sm" onClick={() => setAddResinOpen(true)} className="text-xs text-blue-600 h-6 px-2" data-testid="add-resin-btn"><Plus size={12} className="mr-1" />Nouvelle résine</Button>
              </div>
              <Select value={selectedResinId} onValueChange={setSelectedResinId}>
                <SelectTrigger className="rounded-sm bg-white" data-testid="resin-select"><SelectValue placeholder="Sélectionner une résine" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {resins.map(r => <SelectItem key={r.id} value={r.id}>{r.brand} — {r.model}{r.reference ? ` (${r.reference})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Color / Pigment */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Couleur *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select value={colorType} onValueChange={v => { setColorType(v); if (v !== 'pigment') setSelectedPigmentId(''); }}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="color-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Blanc">Blanc</SelectItem>
                    <SelectItem value="Transparent">Transparent</SelectItem>
                    <SelectItem value="pigment">Couleur via pigment</SelectItem>
                  </SelectContent>
                </Select>
                {colorType === 'pigment' && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-500">Pigment *</span>
                      <Button variant="ghost" size="sm" onClick={() => setAddPigmentOpen(true)} className="text-xs text-blue-600 h-5 px-1" data-testid="add-pigment-btn"><Plus size={10} className="mr-0.5" />Nouveau</Button>
                    </div>
                    <Select value={selectedPigmentId} onValueChange={setSelectedPigmentId}>
                      <SelectTrigger className="rounded-sm bg-white" data-testid="pigment-select"><SelectValue placeholder="Sélectionner un pigment" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {pigments.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.color_code ? ` (${p.color_code})` : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Models */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Modèles fabriqués *</Label>
                <Button variant="ghost" size="sm" onClick={() => setAddModelOpen(true)} className="text-xs text-blue-600 h-6 px-2" data-testid="add-model-btn"><Plus size={12} className="mr-1" />Nouveau modèle</Button>
              </div>
              <div className="space-y-2 border rounded-sm p-3 bg-zinc-50">
                {batchModels.map(m => (
                  <div key={m.id} className="flex items-center gap-3">
                    <Checkbox
                      checked={!!selectedModels[m.id]}
                      onCheckedChange={() => toggleModel(m.id, m.name)}
                      id={`model-${m.id}`}
                      data-testid={`model-check-${m.name}`}
                    />
                    <label htmlFor={`model-${m.id}`} className="font-medium text-sm flex-1 cursor-pointer">{m.name}</label>
                    {selectedModels[m.id] && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">Qté:</span>
                        <Input
                          type="number"
                          min="1"
                          value={selectedModels[m.id].quantity}
                          onChange={e => setModelQty(m.id, e.target.value)}
                          className="w-20 h-8 rounded-sm text-center"
                          data-testid={`model-qty-${m.name}`}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {batchModels.length === 0 && <p className="text-sm text-zinc-400 text-center py-2">Aucun modèle disponible</p>}
              </div>
              {totalPieces > 0 && <p className="text-sm font-medium text-zinc-700 mt-2">Total pièces : <strong>{totalPieces}</strong></p>}
            </div>

            {/* Post-treatment */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Post-traitement *</Label>
              <Select value={postTreatment} onValueChange={setPostTreatment}>
                <SelectTrigger className="mt-1 rounded-sm bg-white" data-testid="post-treatment-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {postTreatments.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Notes (optionnel)</Label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 rounded-sm" placeholder="Remarques sur le lot..." data-testid="batch-notes" />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="submit-batch-btn">
              {submitting ? 'Création...' : 'Créer le lot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Resin Sub-Dialog */}
      <Dialog open={addResinOpen} onOpenChange={setAddResinOpen}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader><DialogTitle>Nouvelle résine</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase text-zinc-500">Marque *</Label><Input value={newResin.brand} onChange={e => setNewResin({...newResin, brand: e.target.value})} className="mt-1 rounded-sm" data-testid="new-resin-brand" /></div>
            <div><Label className="text-xs uppercase text-zinc-500">Nom *</Label><Input value={newResin.model} onChange={e => setNewResin({...newResin, model: e.target.value})} className="mt-1 rounded-sm" data-testid="new-resin-model" /></div>
            <div><Label className="text-xs uppercase text-zinc-500">Référence</Label><Input value={newResin.reference} onChange={e => setNewResin({...newResin, reference: e.target.value})} className="mt-1 rounded-sm" data-testid="new-resin-ref" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAddResinOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleAddResin} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-resin-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Pigment Sub-Dialog */}
      <Dialog open={addPigmentOpen} onOpenChange={setAddPigmentOpen}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader><DialogTitle>Nouveau pigment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase text-zinc-500">Nom *</Label><Input value={newPigment.name} onChange={e => setNewPigment({...newPigment, name: e.target.value})} className="mt-1 rounded-sm" data-testid="new-pigment-name" /></div>
            <div><Label className="text-xs uppercase text-zinc-500">Référence</Label><Input value={newPigment.reference} onChange={e => setNewPigment({...newPigment, reference: e.target.value})} className="mt-1 rounded-sm" data-testid="new-pigment-ref" /></div>
            <div><Label className="text-xs uppercase text-zinc-500">Code couleur</Label><Input value={newPigment.color_code} onChange={e => setNewPigment({...newPigment, color_code: e.target.value})} placeholder="#FFB6C1" className="mt-1 rounded-sm" data-testid="new-pigment-code" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAddPigmentOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleAddPigment} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-pigment-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Model Sub-Dialog */}
      <Dialog open={addModelOpen} onOpenChange={setAddModelOpen}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader><DialogTitle>Nouveau modèle</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase text-zinc-500">Nom *</Label><Input value={newModel.name} onChange={e => setNewModel({...newModel, name: e.target.value})} className="mt-1 rounded-sm" data-testid="new-model-name" /></div>
            <div><Label className="text-xs uppercase text-zinc-500">Description</Label><Input value={newModel.description} onChange={e => setNewModel({...newModel, description: e.target.value})} className="mt-1 rounded-sm" data-testid="new-model-desc" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAddModelOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleAddModel} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-model-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
